import { SECTION } from "constants/SettingsConstants";

// Service types the Submission Builder applies to. NTMSA, MSA-111 and Pre-MSA
// Snapshot are never submitted to CMS (see MSA_TYPES_WITHOUT_CMS_SUBMISSION).
// Amended Review Screening is deliberately absent from both lists: it keeps
// its CMS Submission tab (it is not in the no-CMS list) but is a screening
// product, not a submission, so the builder never shows for it.
export const SUBMISSION_BUILDER_SERVICE_TYPES: readonly string[] = [
  SECTION.MSA,
  SECTION.AMENDED_REVIEW,
];

export const serviceTypeHasSubmissionBuilder = (
  serviceType: string | null | undefined,
): boolean =>
  !!serviceType && SUBMISSION_BUILDER_SERVICE_TYPES.includes(serviceType);

// Readiness state of one checklist row. Everything is "pending" in the
// skeleton; readiness is computed by a later piece of work.
export type ChecklistItemStatus =
  | "complete"
  | "needs_review"
  | "missing"
  | "not_applicable"
  | "pending";

export type ChecklistRequirement = "required" | "recommended" | "conditional";

export type ChecklistItem = {
  // two-digit CMS reference guide section prefix (Ref. Guide Table 10-1);
  // also the output file-name prefix
  section: string;
  title: string;
  requirement: ChecklistRequirement;
  description: string;
  status: ChecklistItemStatus;
};

// Auto-generated artefacts the builder will produce from file data.
export type AutoGenerateItem = {
  section: string;
  title: string;
};

export type ThresholdCheckRow = {
  label: string;
  value: string;
};

export type CoverLetterDataRow = {
  label: string;
  status: ChecklistItemStatus;
};

// Submission checklist. Order follows the mockup's review priority, not the
// section number: section 25 (settlement docs) sits after the record and
// payment-history rows on purpose. Statuses are placeholders until the
// readiness computation lands.
export const SUBMISSION_CHECKLIST: readonly ChecklistItem[] = [
  {
    section: "05",
    title: "Submission (Cover) Letter",
    requirement: "required",
    description: "Auto-generated from MSA + cover-letter data panel.",
    status: "pending",
  },
  {
    section: "10",
    title: "Consent to Release",
    requirement: "required",
    description: "Signed consent with beneficiary-review / initials language.",
    status: "pending",
  },
  {
    section: "15",
    title: "Rated Ages — all vendors",
    requirement: "required",
    description:
      "Every rated age report obtained, plus the RA Verification letter.",
    status: "pending",
  },
  {
    section: "20",
    title: "MSA Report (final)",
    requirement: "required",
    description: "Final MSA allocation report.",
    status: "pending",
  },
  {
    section: "35",
    title: "Medical Records — tagged in Metadoc",
    requirement: "required",
    description:
      "Records tagged for submission in the Metadoc, bundled per treatment year.",
    status: "pending",
  },
  {
    section: "40",
    title: "Medical Payment History",
    requirement: "required",
    description: "Itemized medical payment history through current date.",
    status: "pending",
  },
  {
    section: "40",
    title: "Prescription Payment History",
    requirement: "required",
    description: "Itemized Rx payments under the claim.",
    status: "pending",
  },
  {
    section: "25",
    title:
      "Settlement Agreement / Proposed / Court Order — or Statement of No Settlement Docs",
    requirement: "required",
    description:
      "Generate the Statement of No Settlement Documents if nothing has been drafted.",
    status: "pending",
  },
  {
    section: "50",
    title: "Affidavit of Denied Conditions",
    requirement: "conditional",
    description: "Required when denied conditions are listed on the MSA.",
    status: "pending",
  },
  {
    section: "50",
    title: "DME Invoices",
    requirement: "conditional",
    description:
      "Required when the allocation includes durable medical equipment.",
    status: "pending",
  },
  {
    section: "50",
    title: "Surgical Worksheet",
    requirement: "recommended",
    description: "Recommended when surgery is in the allocation.",
    status: "pending",
  },
  {
    section: "50",
    title: "Treating Physician Questionnaire (TPQ)",
    requirement: "conditional",
    description: "Becomes required if one has ever been obtained on the file.",
    status: "pending",
  },
  {
    section: "50",
    title: "First Report of Injury",
    requirement: "recommended",
    description: "Tagged in the Document Drawer at intake.",
    status: "pending",
  },
  {
    section: "50",
    title: "Indemnity & Expense Payment History · Retail Rx History",
    requirement: "recommended",
    description:
      "Indemnity / expense satisfied by the client pay list; retail Rx on request.",
    status: "pending",
  },
];

export const AUTO_GENERATE_ITEMS: readonly AutoGenerateItem[] = [
  { section: "05", title: "Submission Letter" },
  { section: "20", title: "MSA Report (final)" },
  { section: "15", title: "Rated Age Verification" },
  { section: "25", title: "Stmt. of No Settlement Docs" },
  { section: "50", title: "Affidavit of Denied Conds." },
  { section: "50", title: "Surgical Worksheet" },
];

export const COVER_LETTER_DATA_ROWS: readonly CoverLetterDataRow[] = [
  { label: "Demographics / Medicare ID or SSN", status: "pending" },
  { label: "Median rated age", status: "pending" },
  { label: "Life expectancy used", status: "pending" },
  { label: "DOI(s) & claim numbers", status: "pending" },
  { label: "Accepted / denied conditions + ICD-10", status: "pending" },
  { label: "MSA total / medical split / Rx split", status: "pending" },
  { label: "Pricing method (state FS)", status: "pending" },
  { label: "Lump sum vs. annuity + seed", status: "pending" },
  { label: "State of jurisdiction / venue", status: "pending" },
  { label: "Administrator (self / prof.)", status: "pending" },
];

export const GATE_RULES: readonly string[] = [
  "Threshold not met → builder hidden, banner shows non-submit rationale.",
  'Any required item red → "Submitted" cannot be set.',
  "Override = Legal/Compliance role only, reason logged to Audit History.",
];

export const CHECKLIST_STATUS_LABEL: Record<ChecklistItemStatus, string> = {
  complete: "Complete",
  needs_review: "Needs review",
  missing: "Missing",
  not_applicable: "N/A",
  pending: "Not evaluated",
};

export const REQUIREMENT_LABEL: Record<ChecklistRequirement, string> = {
  required: "Required",
  recommended: "Recommended",
  conditional: "Conditional",
};
